/**
 * 홈(public) 레이아웃 전역 초기화.
 * Lenis smooth scroll, GSAP 애니메이션, 드로어, 탑버튼 등 홈 전용 모듈을 초기화한다.
 */
import { AosInitializer } from "../home/global/AosInitializer";
import { getScrollTrigger } from "../home/global/GsapGlobals";
import { ScrollRevealController } from "../home/global/ScrollRevealController";
import { ParallaxImageController } from "../home/global/ParallaxImageController";
import { ClipPathRevealController } from "../home/global/ClipPathRevealController";
import { SplitCharsService } from "../home/global/SplitCharsService";
import { SubVisualTitleSplitController } from "../home/global/SubVisualTitleSplitController";
import { SmoothScrollManager } from "../home/global/SmoothScrollManager";
import { VISUAL_EFFECTS } from "../home/global/VisualEffectsConfig";
import { DrawerController } from "../home/global/DrawerController";
import { SearchOverlayController } from "../home/global/SearchOverlayController";
import { LangSwitcherController } from "../home/global/LangSwitcherController";
import { FilterSidebarController } from "../home/global/FilterSidebarController";
import { PublicListController } from "../home/global/PublicListController";
import { FloatingCtaController } from "../home/global/FloatingCtaController";
import { QuickContactBarController } from "../home/global/QuickContactBarController";
import { AssetRequestPopupController } from "../home/global/AssetRequestPopupController";
import { SubNavSwiper } from "../home/global/SubNavSwiper";
import { UiTabsSwiper } from "../home/global/UiTabsSwiper";
import { RelatedPostsSwiper } from "../home/global/RelatedPostsSwiper";
import { ShareModal } from "../home/global/ShareModal";
// Phase 2 / Sprint A — 시그니처 인터랙션 (data-anim="*" 트리거)
import { TypeMaskRevealController } from "../home/global/TypeMaskRevealController";
import { MarqueeWhisperController } from "../home/global/MarqueeWhisperController";
import { GradientMeshHero } from "../home/global/GradientMeshHero";
import { MagneticCardController } from "../home/global/MagneticCardController";
// 2026-05-28 — 정체성 섹션 12종 (Swiper + GSAP ScrollTrigger 통합 컨트롤러)
import { UiSectionsController } from "../home/sections/UiSectionsController";
import { HeroTitleStaggerController } from "../home/sections/HeroTitleStaggerController";
import { ThehHeroIntroController } from "../home/sections/ThehHeroIntroController";
import { ThehMetricRollController } from "../home/sections/ThehMetricRollController";
import { ThehWhyRevealController } from "../home/sections/ThehWhyRevealController";
import { ThehSolutionProgressController } from "../home/sections/ThehSolutionProgressController";
import { HomeLocationController } from "../home/sections/HomeLocationController";
// 2026-07-13 — WHO WE ARE 전용 핀/시그니처 섹션 (내부 data-section 가드)
import { WhoWeAreHorizonController } from "../home/sections/WhoWeAreHorizonController";
import { WhoWeAreSignatureController } from "../home/sections/WhoWeAreSignatureController";
import { NavDropdownController } from "../home/global/NavDropdownController";
import { PopupManager } from "../home/popup/PopupManager";
import { SliderAudit } from "../home/dev/SliderAudit";

export class HomeLayout {
    static init(): void {
        const reduceMotion = HomeLayout.prefersReducedMotion();

        HeroTitleStaggerController.init();
        AosInitializer.init();
        // 시각효과는 VisualEffectsConfig 토글로 가드(잔김 대응 — 무거운 효과 기본 OFF).
        if (VISUAL_EFFECTS.fadeUp) ScrollRevealController.initFadeUp();
        if (VISUAL_EFFECTS.blur) ScrollRevealController.initBlur();
        if (VISUAL_EFFECTS.listUp) ScrollRevealController.initListUp();
        if (VISUAL_EFFECTS.parallax) ParallaxImageController.init();
        if (VISUAL_EFFECTS.clipReveal) ClipPathRevealController.init();
        SplitCharsService.expose();
        SubVisualTitleSplitController.init();

        if (!reduceMotion) {
            SmoothScrollManager.init();
        } else {
            console.info('[HomeLayout] SmoothScrollManager skipped: prefers-reduced-motion is active');
        }

        DrawerController.init();
        NavDropdownController.init();
        SearchOverlayController.init();
        LangSwitcherController.init();
        FilterSidebarController.init();
        PublicListController.init();
        FloatingCtaController.init();
        QuickContactBarController.init();
        AssetRequestPopupController.init();
        SubNavSwiper.init();
        UiTabsSwiper.init();
        RelatedPostsSwiper.init();
        ShareModal.init();

        // 시그니처 인터랙션 — 각 컨트롤러는 내부에서 motionOK/isImmersiveCapable 가드
        TypeMaskRevealController.init();
        MarqueeWhisperController.init();
        GradientMeshHero.init();
        MagneticCardController.init();

        // 홈 전용 진입 모션은 실제 DOM 순서(Hero → Why → Lab)대로 등록한다.
        UiSectionsController.init();
        ThehHeroIntroController.init();
        ThehWhyRevealController.init();
        ThehMetricRollController.init();
        ThehSolutionProgressController.init();
        HomeLocationController.init();

        // WHO WE ARE 전용 핀 섹션 (각 컨트롤러가 data-section 존재 시에만 동작)
        WhoWeAreHorizonController.init();
        WhoWeAreSignatureController.init();
        HomeLayout.syncScrollTriggerOrder();

        // 지연/동적 삽입된 슬라이더도 초기화되도록 재초기화 훅 연결.
        // (각 init 은 bound 가드로 idempotent — Engine 2 의 template:components-init 을 공유 신호로 사용)
        window.addEventListener("template:components-init", () => {
            SubNavSwiper.init();
            UiTabsSwiper.init();
            RelatedPostsSwiper.init();
            UiSectionsController.reinitSwipers();
        });

        // 사이트 팝업 엔진 (전 페이지 전역 — 내부에서 [data-popups-root] 없으면 즉시 반환)
        PopupManager.init();

        // 슬라이더 초기화 진단 도구 — window.__sliderAudit() / ?slider-audit=1
        SliderAudit.init();
    }

    private static prefersReducedMotion(): boolean {
        if (document.documentElement.getAttribute("data-rm-aware") !== "1") return false;
        return (
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
    }

    private static syncScrollTriggerOrder(): void {
        const sync = (): void => {
            requestAnimationFrame(() => {
                const ScrollTrigger = getScrollTrigger();
                ScrollTrigger.sort();
                ScrollTrigger.refresh();
            });
        };

        if (document.readyState === "complete") {
            sync();
        } else {
            window.addEventListener("load", sync, { once: true });
        }
    }
}
